import { AuthDispatchContext } from "@context/AuthContext";
import useApiHook from "@hooks/useApiHook";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "@axios/axiosInstance";
import { LoginContext } from "@context/LoginContext";
import useApiError from "@hooks/useApiError";
import useNotification from "@src/hooks/useNotification";
import Cookies from "js-cookie";
import { CartIdContext } from "@src/context/CartContext";
import { validateEmailVal } from "@src/utils/Regex";
import { Tooltip } from "react-tooltip";
import inputBgMd from "@assets/images/backgrounds/inputBgMd.webp";
import {
  getGuestCartTokenPayloadString,
  removeGuestCartToken,
} from "@src/utils/CartItemsCountsHelper";
import useReactQuery from "@src/hooks/useReactQuery";
import useIsMobile from "@src/hooks/useIsMobile";
import type { ILogin } from "@src/types/AppTypes";

const Login = React.forwardRef<HTMLDivElement, ILogin>(
  (
    {
      loginModal,
      modalBorder,
      setLoginModal,
      setSignUpModal,
      setForgotModal,
      handleConfirmationRes,
    },
    ref
  ) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isMobile = useIsMobile();
    const { cartId, setCartId } = useContext(CartIdContext);
    const quoteEndpoint = useApiHook({
      method: "get",
      url: "/users/login_messages",
    });

    const setApiError = useApiError();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dispatchAuthContext = useContext(AuthDispatchContext);
    const navigate = useNavigate();
    const loginEndPoint = useApiHook({
      url: "/users/sessions",
      method: "post",
    });
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const { setLogin } = useContext(LoginContext);
    const [rememberMe, setRememberMe] = useState<boolean>(false);
    const flashNotification = useNotification();

    const [error, setError] = useState<{
      type: "email" | "password";
      value: string;
    } | null>(null);

    const submit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!email.trim()) {
        setError({ type: "email", value: "Please enter your email address." });
        return;
      }
      if (!validateEmailVal(email)) {
        setError({
          type: "email",
          value: "Please enter a valid email address.",
        });
        return;
      }
      if (!password.trim().length) {
        setError({ type: "password", value: "Please enter your password." });
        return;
      }

      try {
        setIsLoading(true);
        const res = await loginEndPoint<{ status: boolean; message: string }>(
          {
            user: {
              email,
              password,
              from_checkout: true,
              cart_id: cartId,
            },
          },
          getGuestCartTokenPayloadString(),
          {
            headers: {
              "Access-Control-Allow-Credentials": true,
            },
            withCredentials: true,
          }
        );
        if (res && res.data.status === true) {
          removeGuestCartToken();
          setLogin({ type: "updateLogin", payload: false });
          setCartId({ type: "guestUserCartId", payload: 0 });
          flashNotification(res.data);
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + res.headers["access-token"];
          const redirect = localStorage.getItem("redirectTo");
          const product = localStorage.getItem("checkoutProduct");
          const quantity = localStorage.getItem("quantity");
          const shippingId = localStorage.getItem("shippingId");
          const orderId = localStorage.getItem("orderId");
          const options: Cookies.CookieAttributes = {};
          if (rememberMe) {
            options.expires = 14 * 24 * 60 * 60;
          }
          Cookies.set("aT", `${res.headers["access-token"]}`, options);
          localStorage.setItem("count", "0");
          dispatchAuthContext({ type: "update", payload: true });
          if (redirect && product && shippingId && quantity && orderId) {
            localStorage.removeItem("redirectTo");
            localStorage.removeItem("checkoutProduct");
            localStorage.removeItem("quantity");
            localStorage.removeItem("shippingId");
            localStorage.removeItem("orderId");
            navigate(redirect, {
              replace: isMobile ? true : false,
              state: {
                checkoutProduct: [
                  {
                    product: JSON.parse(product),
                    quantity: quantity,
                    id: -100,
                  },
                ],
                quantity,
                shippingId,
                orderId,
              },
            });
          } else {
            const redirectTo = Cookies.get("redirectTo");
            if (redirectTo) {
              Cookies.remove("redirectTo");
              navigate(redirectTo, {
                replace: isMobile ? true : false,
              });
            } else {
              setTimeout(() => {
                navigate(location.pathname, {
                  replace: isMobile ? true : false,
                });
              }, 100);
            }
          }
        }
        if (res && res.data.status === false) {
          flashNotification(res.data);
        }
      } catch (e: any) {
        if (e.response.data.varified !== undefined) {
          if (!e.response.data.verified) {
            handleConfirmationRes(email);
            setTimeout(() => {
              setLogin({ type: "updateLogin", payload: false });
            });
          }
        } else {
          document.getElementById("login-form")?.classList.add("form-shake");
          setApiError(e);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const {
      data: loginMessageData,
      isSuccess: loginMessageIsSuccess,
      error: loginMessageError,
      isLoading: loginMessageIsLoading,
    } = useReactQuery<{
      status: boolean;
      message: string;
      data: {
        show_on: string;
        id: number;
        content: string;
        created_at: string;
        updated_at: string;
      };
    }>({
      args: { queryKey: ["get-login-message"] },
      queryFn: () => quoteEndpoint(null, "", null),
    });

    useEffect(() => {
      if (loginMessageError) {
        setApiError(loginMessageError);
      }
    }, [
      loginMessageIsSuccess,
      loginMessageError,
      loginMessageData,
      setApiError,
    ]);

    // useEffect(() => {
    //   let timer: ReturnType<typeof setTimeout>;
    //   if (error) {
    //     timer = setTimeout(() => setError(null), 3000);
    //   }

    //   return () => {
    //     clearTimeout(timer);
    //   };
    // }, [error]);

    useEffect(() => {
      const header = document.getElementById("header");
      const footer = document.getElementById("footer");

      document.body.style.overflow = "hidden";

      if (header) {
        header.style.zIndex = "0";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }

      return () => {
        // Enable background scroll
        document.body.style.overflow = "auto";

        if (header) {
          header.style.zIndex = "";
        }
        if (footer) {
          footer.style.zIndex = "";
        }
      };
    }, []);

    // const [scrollPosition, setScrollPosition] = useState(0);

    useEffect(() => {
      const header = document.getElementById("header");
      const footer = document.getElementById("footer");

      document.body.style.overflow = "hidden";
      document.body.style.width = "100%";
      document.body.style.touchAction = "none";

      if (header) {
        header.style.zIndex = "2";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }
      return () => {
        // Enable background scroll
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "auto";
        document.body.style.width = "";
        document.body.style.touchAction = "";

        if (header) {
          header.style.zIndex = "";
        }
        if (footer) {
          footer.style.zIndex = "";
        }
      };
    }, []);

    return (
      <div
        className={`fixed size-full bg-[#00000080] top-0 left-0 grid place-items-center z-[9999] modal-content overflow-hidden ${
          loginModal ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <div className="w-full overflow-auto h-full grid place-items-center">
          <div
            id={"login-form-container"}
            onAnimationEnd={() => {
              if (document.getElementById("resend-modal")) {
                return;
              }
              document
                .getElementById("login-form-container")
                ?.classList.remove("form-shake");
            }}
            ref={ref}
            className="max-w-2xl max-md:max-w-[95%] max-md:mx-auto w-full bg-gradient-to-b from-[#1D1159] to-[#00AEEF] relative rounded-[5px_30px_40px_0px]"
          >
            <img
              src={modalBorder}
              className="absolute top-0 left-0 size-full z-[0]"
              alt=""
            />
            <div className="relative z-10 py-16 max-md:py-4 px-32 max-md:px-8 max-sm:px-4">
              <button
                onClick={() => {
                  // removed this keys here, if user click on wishlist button in prelogin mode and then close login modal the need to remove these keys
                  sessionStorage.removeItem("selMusId"); //remove selected music id, which we stored before login
                  sessionStorage.removeItem("selEpId"); //remove selected episode id, which we stored before login
                  sessionStorage.removeItem("selProId"); //remove selected episode id, which we stored before login
                  sessionStorage.removeItem("selProVarId"); //remove selected episode id, which we stored before login

                  localStorage.removeItem("lID");
                  localStorage.removeItem("letype");
                  localStorage.removeItem("lesubtype");

                  setLoginModal({
                    payload: !loginModal,
                    type: "updateLogin",
                  });
                  if (isMobile) {
                    searchParams.delete("tab");
                    setSearchParams(searchParams, { replace: true });
                  }
                }}
                className="text-primary absolute top-10 max-[512px]:top-5 right-10 max-[512px]:right-5 text-3xl max-md:text-2xl ml-auto block duration-150 hover:scale-95"
                aria-label="Close"
              >
                <i className="ri-close-circle-line"></i>
              </button>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-primary text-3xl max-md:text-xl">
                  Welcome Back
                </h1>
                <span className="text-3xl wave-hand">👋</span>
              </div>
              {loginMessageIsLoading ? (
                <div className="skeleton w-full h-5 !rounded my-4"></div>
              ) : (
                <span
                  className="text-secondary text-xl max-md:text-lg break-word"
                  title={loginMessageData?.data?.data?.content ?? ""}
                >
                  {loginMessageData?.data?.data?.content ?? ""}
                </span>
              )}
              <form
                onSubmit={submit}
                onAnimationEnd={() => {
                  document
                    .getElementById("login-form")
                    ?.classList.remove("form-shake");
                }}
                className="my-10 max-md:my-4"
                id="login-form"
              >
                <div className="mb-6 max-md:mb-4">
                  <label
                    className="block text-secondary text-base f-family-mochiy-pop mb-2"
                    htmlFor="loginemail"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <input
                      autoComplete="email webauthn"
                      id="loginemail"
                      className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary p-[1.6rem] max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                      type="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value), setError(null);
                      }}
                      autoCapitalize="none"
                      inputMode="email"
                    />
                    <img
                      className="absolute top-0 left-0 size-full"
                      src={inputBgMd}
                      alt=""
                    />
                  </div>
                  {error?.type === "email" && error.value ? (
                    <p className="text-red-500 mt-1 text-base font-medium f-family-mochiy-pop w-full">
                      {error.value}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label
                    className="block text-secondary text-base f-family-mochiy-pop mb-2"
                    htmlFor="loginPassword"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary z- p-[1.6rem] pr-12 max-md:pr-12 max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                      id="loginPassword"
                      type={showPassword ? "text" : "password"}
                      autoComplete="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <img
                      className="absolute top-0 left-0 size-full"
                      src={inputBgMd}
                      alt=""
                    />
                    <button
                      type="button"
                      className="absolute right-5 top-2/4 -translate-y-2/4 z-[11]"
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                    >
                      <i
                        className={`${
                          showPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                        } text-2xl`}
                      ></i>
                    </button>
                  </div>
                  {error?.type === "password" && error.value && (
                    <p className="text-red-500 mt-1 text-base font-medium f-family-mochiy-pop w-full">
                      {error?.type === "password" && error.value}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <label
                      htmlFor="rememberMe"
                      className="f-family-mochiy-pop cursor-pointer text-base flex items-center gap-1 max-[512px]:text-xs text-[#1D1159] hover:text-primary"
                    >
                      <input
                        id="rememberMe"
                        className="peer custom-checkbox cursor-pointer disabled:cursor-not-allowed"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => {
                          if (e.target.checked) {
                            localStorage.setItem(
                              "rememberMe",
                              `${e.target.checked}`
                            );
                          } else {
                            localStorage.removeItem("rememberMe");
                          }
                          setRememberMe(e.target.checked);
                        }}
                        disabled={Cookies.get("rememberMe") === "false"}
                      />
                      Remember me
                      <i
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        data-tooltip-id="remember-me-tooltip"
                        className="ri-information-line h-5 max-[512px]:pt-1"
                        data-tooltip-content={
                          "Enable 'Remember Me' to stay logged in for 14 days without re-entering your credentials. Tokens are securely stored for easy login."
                        }
                      ></i>
                      <Tooltip
                        style={{
                          backgroundColor: "#ffbff7",
                          color: "black",
                          zIndex: 99,
                          fontSize: 14,
                          width: "240px",
                        }}
                        place="top"
                        className="break-words"
                        id="remember-me-tooltip"
                      />
                    </label>
                    <button
                      type="button"
                      className="text-[#1D1159] f-family-mochiy-pop inline-block text-sm max-[512px]:text-xs duration-150 hover:text-primary max-sm:hover:text-[#1d1159] max-sm:active:!text-primary login-form"
                      onClick={() => {
                        setLoginModal({ type: "updateLogin", payload: false });
                        setForgotModal(true);
                        if (isMobile) {
                          searchParams.set("tab", "forgot-password");
                          setSearchParams(searchParams, { replace: true });
                        }
                      }}
                      aria-label="Forgot Password"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>
                <div className="px-7 max-sm:px-0">
                  {/* <button
                  disabled={isLoading}
                  type="submit"
                  className="flex justify-center text-2xl w-full mt-5 p-[1.3rem] rounded-[5px_27px_19px_25px] bg-cover bg-no-repeat bg-[url('@assets/images/btnBgImage.webp')] duration-150 hover:scale-95 disabled:opacity-70 disabled:hover:cursor-not-allowed"
                  aria-label="Login"
                >
                  {isLoading ? (
                    <i className="ri-loop-right-line btn-loader"></i>
                  ) : (
                    "Log in"
                  )}
                </button> */}
                  <button
                    disabled={
                      isLoading || !email.trim().length || password.length < 8
                    }
                    type="submit"
                    className="w-full mt-5 text-2xl max-md:text-xl customized-button rounded-[5px_20px_11px_20px] px-10 max-md:px-5"
                    aria-label="Login"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center w-full">
                        <i className="ri-loop-right-line btn-loader"></i>
                      </div>
                    ) : (
                      "Log in"
                    )}
                  </button>
                </div>
              </form>
              <h5 className="text-lg max-sm:text-base text-center block text-white login-form">
                DON'T HAVE AN ACCOUNT YET?{" "}
                <button
                  className="inline-block duration-150 text-[#1D1159] hover:text-primary"
                  onClick={() => {
                    setSignUpModal({ type: "updateSignup", payload: true });
                    setLoginModal({ payload: false, type: "updateLogin" });
                    if (isMobile) {
                      searchParams.set("tab", "signup");
                      setSearchParams(searchParams, { replace: true });
                    }
                  }}
                  aria-label="Sign up"
                >
                  SIGN UP
                </button>
              </h5>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default Login;
